import { Card, CardContent, Stack, Typography } from '@mui/material'
import { EquipmentType, OperationalEquipmentDto, WorkAreaType } from '@operations/app/api'
import { useTranslate } from '@tolgee/react'
import { useMinimalsTheme } from '@tom-ui/ui'
import { IFormStore } from '@tom-ui/utils'
import { observer } from 'mobx-react-lite'
import { useMemo } from 'react'
import {
  mapFromOperationEquipmentsToCarrierVisitAssignments,
  mapFromOperationEquipmentsToYardBlockAssignments,
} from '../../helpers/operational-equipment.mapper'
import { IOperationalEquipmentFormData } from '../../models/operational-equipment-assignment.model'
import { OperationalEquipmentClearAll } from '../molecules/OperationalEquipmentClearAll'
import { OperationalEquipmentStatusSubForm } from '../molecules/OperationalEquipmentStatusSubForm'
import { OperationalEquipmentTrainAssignmentSubForm } from '../molecules/OperationalEquipmentTrainAssignmentSubForm'
import { OperationalEquipmentVesselAssignmentSubForm } from '../molecules/OperationalEquipmentVesselAssignmentSubForm'
import { OperationalEquipmentYardAssignmentSubForm } from '../molecules/OperationalEquipmentYardAssignmentSubForm'

interface Props {
  formId: string
  operationalEquipments: OperationalEquipmentDto[]
  formStore: IFormStore<IOperationalEquipmentFormData>
}
export const OperationalEquipmentForm = observer(
  ({ operationalEquipments, formId, formStore }: Props) => {
    const { t } = useTranslate()
    const theme = useMinimalsTheme()

    const canSeeCarrierAssignment = useMemo(() => {
      const equipmentType = operationalEquipments[0]?.equipmentType
      const possibleTypes: EquipmentType[] = [EquipmentType.Rs, EquipmentType.Ech, EquipmentType.Tt]

      return equipmentType && possibleTypes.includes(equipmentType)
    }, [operationalEquipments])

    const canSeeYardBlockAssignment = useMemo(() => {
      const equipmentType = operationalEquipments[0]?.equipmentType
      const possibleTypes: EquipmentType[] = [
        EquipmentType.Rs,
        EquipmentType.Ech,
        EquipmentType.Sts,
        EquipmentType.Rtg,
        EquipmentType.Rmg,
      ]

      return equipmentType && possibleTypes.includes(equipmentType)
    }, [operationalEquipments])

    return (
      <Card>
        <CardContent>
          <Stack
            id={formId}
            component='form'
            noValidate
            autoComplete='off'
            gap={theme.customSpacing.l}
          >
            <Stack flexDirection='row' justifyContent='space-between' alignItems='center'>
              <Typography variant='subtitle1'>{t('assignments', 'Assignments')}</Typography>
              <OperationalEquipmentClearAll onConfirm={formStore.clearAll} />
            </Stack>

            {canSeeCarrierAssignment && (
              <>
                <OperationalEquipmentVesselAssignmentSubForm<IOperationalEquipmentFormData>
                  name={'vesselAssignments'}
                  formStore={formStore}
                  equipmentCount={operationalEquipments.length}
                  equipmentType={operationalEquipments[0]?.equipmentType}
                  initialSelectedAssignments={mapFromOperationEquipmentsToCarrierVisitAssignments(
                    operationalEquipments,
                    WorkAreaType.Crane,
                  )}
                />

                <OperationalEquipmentTrainAssignmentSubForm<IOperationalEquipmentFormData>
                  name={'trainAssignments'}
                  formStore={formStore}
                  equipmentCount={operationalEquipments.length}
                  equipmentType={operationalEquipments[0]?.equipmentType}
                  initialSelectedAssignments={mapFromOperationEquipmentsToCarrierVisitAssignments(
                    operationalEquipments,
                    WorkAreaType.Train,
                  )}
                />
              </>
            )}

            {canSeeYardBlockAssignment && (
              <OperationalEquipmentYardAssignmentSubForm<IOperationalEquipmentFormData>
                name={'yardAssignments'}
                formStore={formStore}
                equipmentCount={operationalEquipments.length}
                equipmentType={operationalEquipments[0]?.equipmentType}
                initialSelectedAssignments={mapFromOperationEquipmentsToYardBlockAssignments(
                  operationalEquipments,
                )}
              />
            )}

            <OperationalEquipmentStatusSubForm<IOperationalEquipmentFormData>
              name={'status'}
              formStore={formStore}
              initialStatus={operationalEquipments[0]?.operationalStatus}
            />
          </Stack>
        </CardContent>
      </Card>
    )
  },
)
